import type { MessageKey } from '../i18n';
import type { Feature } from './experience';

/**
 * The tabs in the dock — and what each one says about itself.
 *
 * The dock is pictures with no words under them, which is what keeps it small
 * enough to float over every screen. The cost is that a picture of a globe does
 * not say *why you would press it*. Jay: *"when anyone clicks any of the tabs at
 * the bottom, or when they go to that tab for the first time, a description of
 * the usefulness of that tab appears right above that little tab area … as they
 * close each definition, then they don't see it again."*
 *
 * So each tab carries one sentence about what it is good for (`tip`), shown in a
 * bubble over the dock until its × is pressed (see TabTip), and never again
 * after that — unless Settings → *Show the tab descriptions again* is pressed.
 *
 * **One list, on purpose.** The dock's buttons and their descriptions are the
 * same rows, so a tab cannot be added without saying what it is for: TypeScript
 * wants a `tip` on every row and both languages want its words.
 *
 * Tabs with a `feature` only show in the modes that include it (Simple mode
 * keeps just the Add button — see app/experience.ts and the note in Shell).
 */
export interface DockTab {
  /** What is remembered once its description has been closed. Never reuse one. */
  id: 'tree' | 'world' | 'timeline' | 'calendar' | 'passedDown';
  to: string;
  /** The tab's name: the button's label, and the title of its description. */
  key: MessageKey;
  icon: string;
  feature?: Feature;
  /** Why you would come here, in a sentence or two. */
  tip: MessageKey;
}

export const DOCK_TABS: readonly DockTab[] = [
  { id: 'tree', to: '/tree', key: 'nav.tree', icon: 'tree', tip: 'tabTip.tree' },
  { id: 'world', to: '/world', key: 'nav.world', icon: 'world', feature: 'tab.world', tip: 'tabTip.world' },
  { id: 'timeline', to: '/timeline', key: 'nav.timeline', icon: 'stories', feature: 'tab.timeline', tip: 'tabTip.timeline' },
  { id: 'calendar', to: '/calendar', key: 'nav.calendar', icon: 'calendar', feature: 'tab.calendar', tip: 'tabTip.calendar' },
  { id: 'passedDown', to: '/passed-down', key: 'nav.passedDown', icon: 'heirloom', feature: 'tab.passedDown', tip: 'tabTip.passedDown' },
];

/** Older addresses that still open a tab (see App): the description follows the screen, not the spelling. */
const ALSO: Readonly<Record<string, DockTab['id']>> = { '/': 'tree', '/stories': 'timeline', '/photos': 'timeline' };

/**
 * The tab a path *is* — or null for everything that is not one: a profile, the
 * recorder, Admin controls. Only the tab's own screen counts, not what is under
 * it (`/people/…` is not the tree), because the description is about the place
 * you are standing in.
 */
export function tabForPath(path: string): DockTab | null {
  const clean = path.length > 1 ? path.replace(/\/+$/, '') : path;
  const id = ALSO[clean];
  return DOCK_TABS.find((tab) => (id ? tab.id === id : tab.to === clean)) ?? null;
}

/** The descriptions still to be shown, given the ones already closed. Unknown ids — a tab that has since gone — are ignored. */
export function openTips(closed: readonly string[]): DockTab['id'][] {
  return DOCK_TABS.map((tab) => tab.id).filter((id) => !closed.includes(id));
}
